// app.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import * as XLSX from "xlsx";
import "./App.css";
import { useMsal } from "@azure/msal-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const API_BASE = "https://ics-flaskapi-wa-prod-fvd9edaxfhdrdyas.southcentralus-01.azurewebsites.net";
const BUCKET_PROFILE = "MATT_V2";

const fmtNumber = (n) =>
  typeof n === "number" ? n.toLocaleString() : (Number(n) || 0).toLocaleString();

const EQUIPMENT_FIELDS = [
  "AHU",
  "Pump",
  "Cooling Tower",
  "DOAS Packaged",
  "DOAS Split",
  "DOAS",
  "Air Cooled Chiller",
  "Water Cooled Chiller",
  "Boiler",
  "VRF",
  "WSHP",
  "FCU",
  "PTAC",
  "RTU",
  "VAV",
  "Split System",
  "Fluid Cooler",
  "PFHX",
];

const colorAt = (i) => `hsl(${(i * 37) % 360}, 70%, 60%)`;

const groupSum = (rows, xField, yFields) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r?.[xField] ?? "Unknown";
    if (!map.has(key)) map.set(key, Object.fromEntries(yFields.map((f) => [f, 0])));
    const obj = map.get(key);
    yFields.forEach((f) => {
      obj[f] += Number(r?.[f] || 0);
    });
  });
  const labels = Array.from(map.keys());
  const sums = Array.from(map.values());
  return { labels, sums };
};

const SummaryCard = React.memo(function SummaryCard({
  totalBids,
  mostCommonBOD,
  mostCommonAHU,
  bucketLabels,
  viewData,
  doasUnclassified,
}) {
  const bucketLabelsSafe = {
    CFM:
      bucketLabels?.CFM || [
        "1,000–4,999",
        "5,000–9,999",
        "10,000–14,999",
        "15,000–19,999",
        "20,000+",
        "Unknown",
      ],
    Tons:
      bucketLabels?.Tons || ["1–49", "50–99", "100–199", "200+", "Unknown"],
  };

  return (
    <div className="card summary-card">
      <h3>Summary</h3>
      <div>
        Total Bids: <strong>{fmtNumber(totalBids)}</strong>
      </div>
      <div>
        Most Common BOD: <strong>{mostCommonBOD}</strong>
      </div>
      <div>
        Most Common AHU Type: <strong>{mostCommonAHU}</strong>
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="muted" style={{ marginBottom: 6 }}>
          CFM Buckets:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {bucketLabelsSafe.CFM.map((o) => {
            const found = viewData.filter((r) => (r.CFM_Bucket || "Unknown") === o)
              .length;
            return found ? (
              <span key={o} className="pill pill-blue">
                {o} ({fmtNumber(found)})
              </span>
            ) : null;
          })}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="muted" style={{ marginBottom: 6 }}>
          Tons Buckets:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {bucketLabelsSafe.Tons.map((o) => {
            const found = viewData.filter((r) => (r.Tons_Bucket || "Unknown") === o)
              .length;
            return found ? (
              <span key={o} className="pill pill-cyan">
                {o} ({fmtNumber(found)})
              </span>
            ) : null;
          })}
        </div>
      </div>

      {doasUnclassified > 0 && (
        <div className="muted" style={{ marginTop: 10, fontStyle: "italic" }}>
          ⚠️ {fmtNumber(doasUnclassified)} bid(s) mention DOAS but aren’t marked
          Packaged/Split — wording may need review.
        </div>
      )}
    </div>
  );
});

const EquipmentCards = React.memo(function EquipmentCards({ totals }) {
  const keys = EQUIPMENT_FIELDS;
  return (
    <div className="stats-grid">
      {keys.map((k) => (
        <div key={k} className="stat-card">
          <div className="stat-label">{k}</div>
          <div className="stat-value">{fmtNumber(Number(totals[k] || 0))}</div>
        </div>
      ))}
    </div>
  );
});

const DataTable = React.memo(function DataTable({
  rows,
  preferredColumnOrder,
  rowsToShow,
}) {
  if (!rows || rows.length === 0) return null;
  const allKeys = Object.keys(rows[0]);
  const orderedKeys = preferredColumnOrder.filter((key) => allKeys.includes(key));

  return (
    <div className="tableWrap">
      <table className="table">
        <thead>
          <tr>{orderedKeys.map((key, i) => <th key={i}>{key}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, rowsToShow).map((row, i) => (
            <tr key={i}>
              {orderedKeys.map((key, j) => {
                const val = row[key];
                const pretty =
                  typeof val === "number" && key !== "Quote Number"
                    ? fmtNumber(val)
                    : val;
                return <td key={j}>{pretty}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

const ChartsGrid = React.memo(function ChartsGrid({
  viewData,
  pieOptions,
  getPieData,
  getTonsPieData,
  pieCFMRef,
  pieTonsRef,
  pieGPMRef,
  pieBODRef,
  makeSliceClick,
  setFilterCFM,
  setFilterTons,
  setFilterGPM,
  bodSetterForChart,
  getBarChartData,
}) {
  return (
    <>
      <div className="charts">
        <div className="chart-card">
          <div className="chart-title">AHU CFM Bucket Distribution</div>
          <Pie
            ref={pieCFMRef}
            data={getPieData("AHU CFM Bucket", "CFM_Bucket", "AHU")}
            options={{
              ...pieOptions,
              onClick: makeSliceClick(pieCFMRef, setFilterCFM),
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div className="chart-card">
          <div className="chart-title">Chiller Tons Bucket Distribution</div>
          <Pie
            ref={pieTonsRef}
            data={getTonsPieData()}
            options={{
              ...pieOptions,
              onClick: makeSliceClick(pieTonsRef, setFilterTons),
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div className="chart-card">
          <div className="chart-title">Pump GPM Bucket Distribution</div>
          <Pie
            ref={pieGPMRef}
            data={getPieData("Pump GPM Bucket", "GPM_Bucket", "Pump")}
            options={{
              ...pieOptions,
              onClick: makeSliceClick(pieGPMRef, setFilterGPM),
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div className="chart-card">
          <div className="chart-title">BOD Manufacturer Distribution</div>
          <Pie
            ref={pieBODRef}
            data={getPieData("BOD", "BOD", null, viewData)}
            options={{
              ...pieOptions,
              onClick: makeSliceClick(pieBODRef, bodSetterForChart),
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
      </div>

      <div className="chart-card" style={{ marginTop: 24 }}>
        <div className="chart-title">
          Total Equipment Units per Bucket (Bar Chart)
        </div>
        <Bar
          data={getBarChartData()}
          options={{
            responsive: true,
            plugins: { legend: { position: "top" } },
          }}
        />
      </div>
    </>
  );
});

function Chatbot({ open, onClose, onSend, messages, setMessages, sending }) {
  const [input, setInput] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (!open) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    const reply = await onSend(text);
    setMessages((m) => [
      ...m,
      { role: "assistant", content: reply || "Sorry, I couldn't generate a summary." },
    ]);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chatbot-window">
      <div className="chatbot-header">
        <span>AI Assistant</span>
        <button className="chatbot-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="chatbot-messages" ref={boxRef}>
        {messages.length === 0 && (
          <div className="chatbot-hint">
            Ask about the current filtered view, e.g. “Which BOD leads this month?” or
            “Summarize trends by engineer.”
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role === "user" ? "me" : "bot"}`}>
            {m.content}
          </div>
        ))}
        {sending && <div className="chat-msg bot">Thinking…</div>}
      </div>
      <div className="chatbot-input-row">
        <textarea
          className="chatbot-input"
          placeholder="Type a question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
        />
        <button
          className="btn btn-primary chatbot-send"
          disabled={sending}
          onClick={handleSend}
        >
          Send
        </button>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { accounts, instance } = useMsal();

  if (accounts.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #0a2a43 0%, #104d75 50%, #0a2a43 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            padding: "2.5rem 2rem",
            borderRadius: "1.25rem",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
          }}
        >
          <img
            src="/ics_logo.png"
            alt="ICS Logo"
            style={{
              width: 90,
              marginBottom: 20,
              borderRadius: 8,
            }}
          />

          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 8,
              color: "#0a2a43",
              fontFamily: "Segoe UI, sans-serif",
            }}
          >
            ICS HVAC Dashboard
          </h2>

          <div
            style={{
              fontSize: 14,
              color: "#555",
              marginBottom: 30,
              fontWeight: 500,
            }}
          >
            Meriton • Integrated Cooling Solutions
          </div>

          <button
            style={{
              padding: "12px 20px",
              background: "#0078D4",
              color: "white",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 600,
              width: "100%",
              transition: "0.2s",
            }}
            onClick={() => instance.loginRedirect()}
            onMouseOver={(e) => (e.currentTarget.style.background = "#005fa3")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#0078D4")}
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  // 🔹 NEW: use MSAL here for username + logout
  const { instance, accounts } = useMsal();
  const username = accounts[0]?.username || "";

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const [jsonData, setJsonData] = useState([]);
  const [selectedAPILocation, setSelectedAPILocation] = useState("");
  const [engineerList, setEngineerList] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [selectedBOD, setSelectedBOD] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiLocations, setApiLocations] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rowsToShow, setRowsToShow] = useState(1000);
  const [bucketLabels, setBucketLabels] = useState(null);
  const [filterCFM, setFilterCFM] = useState("");
  const [filterTons, setFilterTons] = useState("");
  const [filterGPM, setFilterGPM] = useState("");
  const [filterBOD, setFilterBOD] = useState("");
  const pieCFMRef = useRef(null);
  const pieTonsRef = useRef(null);
  const pieGPMRef = useRef(null);
  const pieBODRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetch(`${API_BASE}/api/locations`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setApiLocations(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAPILocation) {
      setJsonData([]);
      setEngineerList([]);
      setSelectedEngineer("");
      setSelectedBOD("");
      return;
    }
    setLoading(true);
    setRowsToShow(1000);
    let url = `${API_BASE}/api/hvac-data?location=${encodeURIComponent(
      selectedAPILocation
    )}&bucket_profile=${encodeURIComponent(BUCKET_PROFILE)}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;

    fetch(url)
      .then((res) => res.json())
      .then((resp) => {
        if (Array.isArray(resp.data)) {
          const data = resp.data;
          setJsonData(data);
          setEngineerList(
            Array.isArray(resp.engineers) ? resp.engineers.slice().sort() : []
          );
          setBucketLabels(resp.bucket_labels || null);
        } else {
          setJsonData([]);
          setEngineerList([]);
          setBucketLabels(null);
        }
        setSelectedEngineer("");
        setSelectedBOD("");
        setFilterBOD("");
      })
      .finally(() => setLoading(false));
  }, [selectedAPILocation, startDate, endDate]);

  const availableEngineers = useMemo(() => {
    let rows = jsonData;
    if (selectedBOD) rows = rows.filter((r) => (r.BOD || "Unknown") === selectedBOD);
    const list = Array.from(new Set(rows.map((r) => r.Engineer || "Unknown"))).sort();
    return list;
  }, [jsonData, selectedBOD]);

  const availableBODs = useMemo(() => {
    let rows = jsonData;
    if (selectedEngineer)
      rows = rows.filter((r) => (r.Engineer || "Unknown") === selectedEngineer);
    const list = Array.from(
      new Set(rows.map((r) => r.BOD || "Unknown").filter(Boolean))
    ).sort();
    return list;
  }, [jsonData, selectedEngineer]);

  useEffect(() => {
    if (selectedEngineer && !availableEngineers.includes(selectedEngineer)) {
      setSelectedEngineer("");
    }
  }, [availableEngineers, selectedEngineer]);

  useEffect(() => {
    if (selectedBOD && !availableBODs.includes(selectedBOD)) {
      setSelectedBOD("");
      setFilterBOD("");
    }
  }, [availableBODs, selectedBOD]);

  const effectiveBOD = selectedBOD || filterBOD;
  const viewData = useMemo(() => {
    return jsonData.filter((r) => {
      if (selectedEngineer && (r.Engineer || "Unknown") !== selectedEngineer) return false;
      if (effectiveBOD && (r.BOD || "Unknown") !== effectiveBOD) return false;
      if (filterCFM && (r.CFM_Bucket || "Unknown") !== filterCFM) return false;
      if (filterTons && (r.Tons_Bucket || "Unknown") !== filterTons) return false;
      if (filterGPM && (r.GPM_Bucket || "Unknown") !== filterGPM) return false;
      return true;
    });
  }, [jsonData, selectedEngineer, effectiveBOD, filterCFM, filterTons, filterGPM]);

  const getPieData = (label, key, countKey = null, source = viewData) => {
    if (!Array.isArray(source)) return { labels: [], datasets: [] };
    const counts = {};
    source.forEach((row) => {
      const bucket = row[key] || "Unknown";
      const addVal = countKey ? Number(row[countKey]) || 0 : 1;
      counts[bucket] = (counts[bucket] || 0) + addVal;
    });
    const labels = Object.keys(counts);
    const values = Object.values(counts);
    return {
      labels,
      datasets: [
        {
          label,
          data: values,
          backgroundColor: labels.map((_, i) => colorAt(i)),
        },
      ],
    };
  };

  const getTonsPieData = () => {
    const counts = {};
    viewData.forEach((row) => {
      const bucket = row["Tons_Bucket"] || "Unknown";
      const totalChillers =
        (Number(row["Air Cooled Chiller"]) || 0) +
        (Number(row["Water Cooled Chiller"]) || 0);
      counts[bucket] = (counts[bucket] || 0) + totalChillers;
    });
    const labels = Object.keys(counts);
    const values = Object.values(counts);
    return {
      labels,
      datasets: [
        {
          label: "Tons Bucket",
          data: values,
          backgroundColor: labels.map((_, i) => colorAt(i)),
        },
      ],
    };
  };

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
              const val = ctx.parsed || 0;
              const pct = ((val / total) * 100).toFixed(1);
              const name = ctx.label || "";
              return `${name}: ${fmtNumber(val)} (${pct}%)`;
            },
          },
        },
      },
    }),
    []
  );

  const makeSliceClick = (ref, setter) => (evt, _els, chart) => {
    const c = ref.current;
    if (!c) return;
    const inst = chart || c;
    const elements = inst.getElementsAtEventForMode(
      evt,
      "nearest",
      { intersect: true },
      true
    );
    if (!elements || !elements.length) return;
    const idx = elements[0].index;
    const label = inst.data?.labels?.[idx] || "Unknown";
    setter((curr) => (curr === label ? "" : label));
  };

  const bodSetterForChart = (updaterFn) => {
    setFilterBOD((curr) => {
      const next = updaterFn(curr);
      setSelectedBOD(next);
      return next;
    });
  };

  const getBarChartData = () => {
    const bucketFields = [
      { bucket: "CFM_Bucket", count: (row) => Number(row["AHU"]) || 0 },
      {
        bucket: "Tons_Bucket",
        count: (row) =>
          (Number(row["Air Cooled Chiller"]) || 0) +
          (Number(row["Water Cooled Chiller"]) || 0),
      },
      { bucket: "GPM_Bucket", count: (row) => Number(row["Pump"]) || 0 },
    ];
    const allLabels = new Set();
    bucketFields.forEach(({ bucket }) =>
      viewData.forEach((row) => {
        if (row[bucket]) allLabels.add(row[bucket]);
      })
    );
    const labels = [...allLabels];
    const datasets = bucketFields.map((field, i) => {
      const map = {};
      labels.forEach((l) => (map[l] = 0));
      viewData.forEach((row) => {
        const b = row[field.bucket];
        const c = field.count(row);
        if (b && Object.prototype.hasOwnProperty.call(map, b)) map[b] += c;
      });
      return {
        label: field.bucket.replace("_Bucket", ""),
        data: labels.map((l) => map[l]),
        backgroundColor: `hsl(${i * 100}, 60%, 60%)`,
      };
    });
    return { labels, datasets };
  };

  const summary = useMemo(() => {
    const totalBids = jsonData.length;
    const bodCounts = {};
    const ahuCounts = {};
    jsonData.forEach((row) => {
      const bod = row["BOD"];
      const ahu = row["ahu_type"];
      if (bod) bodCounts[bod] = (bodCounts[bod] || 0) + 1;
      if (ahu) ahuCounts[ahu] = (ahuCounts[ahu] || 0) + 1;
    });
    const mostCommonBOD =
      Object.entries(bodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "Unknown";
    const mostCommonAHU =
      Object.entries(ahuCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "Unknown";
    return { totalBids, mostCommonBOD, mostCommonAHU };
  }, [jsonData]);

  const equipmentTotals = useMemo(() => {
    const totals = {};
    EQUIPMENT_FIELDS.forEach((key) => {
      totals[key] = viewData.reduce((a, row) => a + (Number(row[key]) || 0), 0);
    });
    return totals;
  }, [viewData]);

  const doasUnclassified = useMemo(() => {
    return viewData.filter(
      (r) =>
        (Number(r["DOAS"]) || 0) > 0 &&
        (Number(r["DOAS Packaged"]) || 0) === 0 &&
        (Number(r["DOAS Split"]) || 0) === 0
    ).length;
  }, [viewData]);

  const byEngineer = useMemo(() => {
    const m = {};
    viewData.forEach((row) => {
      const key = row["Engineer"] || "Unknown";
      m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [viewData]);

  const byBOD = useMemo(() => {
    const m = {};
    viewData.forEach((row) => {
      const key = row["BOD"] || "Unknown";
      m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [viewData]);

  const preferredColumnOrder = [
    "Bid Due Date",
    "Engineer",
    "Scope Of Work",
    "BOD",
    "Quote Number",
    "Job Name",
    "Location",
    "Est. Sell Price",
  ];

  const exportToExcel = () => {
    if (!viewData || viewData.length === 0) return;
    const orderedData = viewData.map((row) => {
      const o = {};
      preferredColumnOrder.forEach((col) => {
        o[col] = row[col] ?? "";
      });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(orderedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, "hvac_dashboard_export.xlsx");
  };

  const clearAllPieFilters = () => {
    setFilterCFM("");
    setFilterTons("");
    setFilterGPM("");
    setFilterBOD("");
    setSelectedBOD("");
  };

  const resetFilters = () => {
    setSelectedAPILocation("");
    setSelectedEngineer("");
    setSelectedBOD("");
    setStartDate("");
    setEndDate("");
    setJsonData([]);
    setRowsToShow(1000);
    clearAllPieFilters();
  };

  const summarizeForChat = async (promptText) => {
    try {
      setChatSending(true);
      const payload = {
        rows: viewData.slice(0, 120),
        question: promptText,
        aggregates: {
          totals: equipmentTotals,
          filters: { filterCFM, filterTons, filterGPM, filterBOD: effectiveBOD },
          location: selectedAPILocation,
          date_range: { startDate, endDate },
          counts: { recordsShown: viewData.length, totalRows: jsonData.length },
          by_engineer: byEngineer,
          by_bod: byBOD,
        },
      };
      const res = await fetch(`${API_BASE}/api/summarize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      return data.summary || "No summary available.";
    } catch (e) {
      return e.message || "Summary failed.";
    } finally {
      setChatSending(false);
    }
  };

  return (
    <RequireAuth>
      <div className="app-root">
        <div className="logo-header">
          <div className="header-left">
            <img src="/ics_logo.png" alt="ICS Logo" className="ics-logo" />
            <h1 className="h1">BIDS Dashboard</h1>
          </div>
          <div className="header-right">
            {username && (
              <div className="user-badge">
                <div className="user-avatar">{username.charAt(0)}</div>
                <span className="user-name">{username}</span>
              </div>
            )}
            <button className="btn-logout" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>

        <div className="controls">
          <div className="control">
            <label>Location:</label>
            <select
              value={selectedAPILocation}
              onChange={(e) => setSelectedAPILocation(e.target.value)}
            >
              <option value="">-- Choose Location --</option>
              {apiLocations.map((loc, idx) => (
                <option key={idx} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          <div className="control">
            <label>Engineer (optional):</label>
            <select
              value={selectedEngineer}
              onChange={(e) => setSelectedEngineer(e.target.value)}
              disabled={availableEngineers.length === 0}
            >
              <option value="">All</option>
              {availableEngineers.map((eng) => (
                <option key={eng} value={eng}>
                  {eng}
                </option>
              ))}
            </select>
          </div>

          <div className="control">
            <label>BOD (optional):</label>
            <select
              value={selectedBOD}
              onChange={(e) => {
                setSelectedBOD(e.target.value);
                setFilterBOD(e.target.value);
              }}
              disabled={availableBODs.length === 0}
            >
              <option value="">All</option>
              {availableBODs.map((bod) => (
                <option key={bod} value={bod}>
                  {bod}
                </option>
              ))}
            </select>
          </div>

          <div className="control">
            <label>Start Date (Bid):</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="control">
            <label>End Date (Bid):</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {jsonData.length > 0 && (
          <div className="tab-nav">
            <button className={`tab-btn${activeTab === "overview" ? " active" : ""}`} onClick={() => setActiveTab("overview")}>📊 Overview</button>
            <button className={`tab-btn${activeTab === "data" ? " active" : ""}`} onClick={() => setActiveTab("data")}>📋 Data Table</button>
            <button className={`tab-btn${activeTab === "charts" ? " active" : ""}`} onClick={() => setActiveTab("charts")}>📈 Charts</button>
          </div>
        )}

        {(filterCFM || filterTons || filterGPM || effectiveBOD || selectedEngineer) && (
          <div className="filter-chips">
            <span className="muted" style={{ fontWeight: 600, alignSelf: "center" }}>Active filters:</span>
            {selectedEngineer && (
              <span className="filter-chip" onClick={() => setSelectedEngineer("")}>
                Engineer: {selectedEngineer} <span className="chip-x">✕</span>
              </span>
            )}
            {effectiveBOD && (
              <span className="filter-chip" onClick={() => { setSelectedBOD(""); setFilterBOD(""); }}>
                BOD: {effectiveBOD} <span className="chip-x">✕</span>
              </span>
            )}
            {filterCFM && (
              <span className="filter-chip" onClick={() => setFilterCFM("")}>
                CFM: {filterCFM} <span className="chip-x">✕</span>
              </span>
            )}
            {filterTons && (
              <span className="filter-chip" onClick={() => setFilterTons("")}>
                Tons: {filterTons} <span className="chip-x">✕</span>
              </span>
            )}
            {filterGPM && (
              <span className="filter-chip" onClick={() => setFilterGPM("")}>
                GPM: {filterGPM} <span className="chip-x">✕</span>
              </span>
            )}
          </div>
        )}

        {viewData.length > 0 && (
          <div className="btn-row">
            <button className="btn btn-primary" onClick={exportToExcel}>
              ⬇️ Export to Excel
            </button>
            <button
              className="btn btn-outline"
              onClick={() =>
                setRowsToShow((n) => Math.min(n + 1000, viewData.length))
              }
            >
              Load 1,000 more
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setRowsToShow(viewData.length)}
            >
              Show all
            </button>
            <button className="btn btn-outline" onClick={resetFilters}>
              Reset filters
            </button>
            <span className="muted">
              Records shown:{" "}
              <strong>{fmtNumber(Math.min(rowsToShow, viewData.length))}</strong> /{" "}
              {fmtNumber(viewData.length)}
            </span>
          </div>
        )}

        {jsonData.length > 0 && activeTab === "overview" && (
          <div className="fade-in-up">
            <div className="section-header">
              <span className="section-icon">📊</span>
              <h2>Dashboard Overview</h2>
            </div>
            <SummaryCard
              totalBids={summary.totalBids}
              mostCommonBOD={summary.mostCommonBOD}
              mostCommonAHU={summary.mostCommonAHU}
              bucketLabels={bucketLabels}
              viewData={viewData}
              doasUnclassified={doasUnclassified}
            />
            <EquipmentCards totals={equipmentTotals} />
          </div>
        )}

        {activeTab === "data" && (
          <div className="fade-in-up">
            <div className="section-header">
              <span className="section-icon">📋</span>
              <h2>Bid Data</h2>
            </div>
            <DataTable
              rows={viewData}
              preferredColumnOrder={[
                "Bid Due Date",
                "Engineer",
                "Scope Of Work",
                "BOD",
                "Quote Number",
                "Job Name",
                "Location",
                "Est. Sell Price",
              ]}
              rowsToShow={rowsToShow}
            />
          </div>
        )}

        {jsonData.length > 0 && activeTab === "charts" && (
          <div className="fade-in-up">
            <div className="section-header">
              <span className="section-icon">📈</span>
              <h2>Visual Analytics</h2>
            </div>
            <ChartsGrid
              viewData={viewData}
              pieOptions={pieOptions}
              getPieData={getPieData}
              getTonsPieData={getTonsPieData}
              pieCFMRef={pieCFMRef}
              pieTonsRef={pieTonsRef}
              pieGPMRef={pieGPMRef}
              pieBODRef={pieBODRef}
              makeSliceClick={makeSliceClick}
              setFilterCFM={setFilterCFM}
              setFilterTons={setFilterTons}
              setFilterGPM={setFilterGPM}
              bodSetterForChart={bodSetterForChart}
              getBarChartData={getBarChartData}
            />
          </div>
        )}

        {!selectedAPILocation && !loading && (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>Welcome to SalesTrak integrated BIDS Dashboard</h3>
            <p>Select a location above to get started</p>
          </div>
        )}

        {loading && (
          <div className="loading-skeleton">
            <div className="skeleton-bar" />
            <div className="skeleton-bar" />
            <div className="skeleton-bar" />
          </div>
        )}

        <button
          className="chatbot-button"
          onClick={() => setChatOpen(true)}
          aria-label="Open AI Assistant"
          title="Ask AI about this view"
        >
          💬
        </button>

        <Chatbot
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          onSend={summarizeForChat}
          messages={chatMessages}
          setMessages={setChatMessages}
          sending={chatSending}
        />
      </div>
    </RequireAuth>
  );
}

export default App;